import {z} from 'zod';
import {isAfter, parse} from 'date-fns';
import {
	makeOptionalPropsNullable,
	LabelValueSchema,
	SchemaValidationErrorLabel,
} from '@netx/core-next/schema';
import {
	AssetSetType,
	DownloadOptionSchema,
	DownloadOptionsFormSchema,
} from '@netx/core-asset-set/schema';

export enum ShareLinkAccessType {
	ANY = 'any',
	ASSET = 'asset',
	CART = 'cart',
	CATEGORY = 'category',
	SAVED_SEARCH = 'savedSearch',
	SAVED_CART = 'savedCart',
	IMPORT = 'import',
	API = 'api',
	ASSET_SET = 'assetSet',
	SESSION = 'session',
}

export enum ShareLinkType {
	DOWNLOAD = 'download',
	VIEW_ONLY = 'viewOnly',
	// we do offer this, via embedding - just not via share links directly
	EMBED = 'embed',
}

export enum ShareLinkStatus {
	ACTIVE = 100,
	USER_DISABLED = 200,
	EXPIRED = 10034,
	SHARE_RESOURCE_MISSING = 10038,
	USER_RESOURCE_MISSING = 10037,
}

export const ShareLinkSourceSchema = z.object({
	ids: z.array(z.number().int()),
	recursive: z.boolean(),
	type: z.nativeEnum(AssetSetType),
});

export const ShareLinkSchema = z.object({
	/** if category, saved cart, or saved search - the id of the resource */
	accessId: z.number().int().gt(0, 'Invalid `id`'),
	accessType: z.nativeEnum(ShareLinkAccessType),
	assetCount: z.number().int().gt(-1, 'Invalid `assetCount`'),
	attributeIds: z.array(z.number().int()),
	creationDate: z.string(),
	expirationDate: z.string().nullable(),
	includeSubFolders: z.boolean(),
	/**
	 * justification details for download approval
	 * @note this will always be null coming from the server
	 * only used to pass details for download approval and
	 * then never is provided back again on the share link
	 */
	justifyDetails: z.array(LabelValueSchema).nullable(),
	/** id of user who provided the justification details */
	justifyId: z.number().int().gt(-1, 'Invalid `justifyId`'),
	lastDownloadedDate: z.string().nullable(),
	/** download options */
	options: z.array(DownloadOptionSchema),
	password: z.string().nullable(),
	/* @readonly */
	passwordProtected: z.boolean(),
	/* @readonly */
	portalContext: z.string().nullable(),
	/** justification requesotr id */
	requestorId: z.number().int().gt(-1, 'Invalid `requestorId`'),
	/** justification requestor name */
	requestorLabel: z.string().nullable(),
	/** `AssetSets` on the way out `ShareLinkSources` on the way back in */
	sources: z.array(ShareLinkSourceSchema),
	status: z.nativeEnum(ShareLinkStatus),
	title: z.string().nonempty(SchemaValidationErrorLabel.REQUIRED),
	/** id */
	token: z.string(), //.nullable(),
	type: z.nativeEnum(ShareLinkType),
	/** creating user id */
	userId: z.number().int().gt(0, 'Invalid `userId`'),
	/** creating user name */
	userLabel: z.string(),
});

export default ShareLinkSchema;

/** validate against a new share link */
export const ShareLinkSchemaNew = makeOptionalPropsNullable(
	ShareLinkSchema.omit({
		creationDate: true,
		lastDownloadedDate: true,
		userId: true,
		userLabel: true,
	}).partial({
		categoryName: true,
		categoryPath: true,
		expirationDate: true,
		notifyId: true,
		notifyLabel: true,
		token: true,
	}),
);

/** all access token fields */
export const ShareLinkField = ShareLinkSchema.keyof().enum;

/** a true access token record */
export type IShareLink = z.infer<typeof ShareLinkSchema>;

/** a new share link record */
export type IShareLinkNew = z.infer<typeof ShareLinkSchemaNew>;

/** refinement for expiration date */
export const ShareLinkExpirationDateSchemaCheck = (
	{expirationDate, status}: Partial<IShareLink>,
	ctx: z.RefinementCtx,
) => {
	if (
		status !== ShareLinkStatus.EXPIRED &&
		expirationDate &&
		!isAfter(parse(expirationDate, 'yyyy-MM-dd', new Date()), new Date())
	) {
		ctx.addIssue({
			fatal: true,
			code: z.ZodIssueCode.invalid_date,
			path: ['expirationDate'],
			message: SchemaValidationErrorLabel.DATE_MUST_BE_IN_THE_FUTURE,
		});
	}
};

/** helper function for wrapping the schema in all refinements */
export const applyShareLinkSchemaEffects = <T extends z.ZodRawShape>(
	schema: z.ZodObject<T>,
) => {
	return schema.superRefine(ShareLinkExpirationDateSchemaCheck);
};

/**
 * default refined schema\
 * note: using this directly will not hit any refinements should any of the initial schema validations fail\
 * if you want the refinements to be hit, regardless of the initial schema validation failing then call the
 * `applyShareLinkSchemaEffects` function directly and supply a `partial` schema
 * @example
 * ```ts
 * applyShareLinkSchemaEffects(ShareLinkSchemaRefined.partial())
 * ```
 */
export const ShareLinkSchemaRefined =
	applyShareLinkSchemaEffects(ShareLinkSchema);

export const ShareLinkFormSchema = DownloadOptionsFormSchema.extend(
	ShareLinkSchema.pick({
		attributeIds: true,
		expirationDate: true,
		password: true,
		status: true,
		title: true,
		type: true,
	}).shape,
);

export type IShareLinkFormValues = z.infer<typeof ShareLinkFormSchema>;
